// client/src/utils/receipt.js
//
// A payment receipt: what it states, and how it prints.
//
// THIS IS A PORT, NOT A COPY-PASTE ACCIDENT. The same three functions live in
// `packages/core/src/portal/admin.js`, where mobile imports them from. The web
// client cannot import that package — packages/core is not a workspace, the
// same constraint already recorded at the top of pageCatalogue.generated.js —
// so the choice was between duplicating ~80 lines of pure arithmetic or
// restructuring the build to make core importable from CRA.
//
// Duplicated deliberately, because the alternative is worse right now: these
// are pure functions over plain objects with no imports, no fetches and no
// framework, so they are the cheapest possible thing to keep in step, and the
// verification below is a unit check either side can run. If the two ever
// disagree, the receipts disagree, and THAT is the bug to look for first.
//
// KEEP IN STEP WITH: packages/core/src/portal/admin.js
//   - feeStatusFor
//   - buildReceipt
//   - receiptHtml
//   - canIssueReceipt

/**
 * Per-student fee status for a structure, or for one INSTALMENT of it.
 *
 * The instalment rule is a WATERFALL: money paid fills instalment 1 before any
 * of it counts towards 2. What a student has paid "for instalment 3" is not
 * their total divided three ways — it is whatever remains after 1 and 2 are
 * satisfied, capped at 3's own amount.
 *
 *   paidForThis = clamp(totalPaid - sumOfEarlierInstalments, 0, thisAmount)
 *
 * Getting this wrong shows a parent as owing nothing on a later instalment
 * while the earlier ones are unpaid — the error that lets a student sit an exam
 * they have not been cleared for.
 */
export function feeStatusFor({ totalPaid, structure, installmentId }) {
  const paidTotal = Number(totalPaid || 0);

  if (!installmentId) {
    const required = Number(structure?.amount || 0);
    const paid = Math.min(paidTotal, required || paidTotal);
    const unpaid = Math.max(required - paidTotal, 0);
    let status = 'not_paid';
    if (paidTotal > 0 && unpaid > 0) status = 'partially_paid';
    if (required > 0 && unpaid <= 0 && paidTotal > 0) status = 'paid';
    return { required, paid, unpaid, status };
  }

  const items = [...(structure?.installments || [])]
    .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0));
  const idx = items.findIndex((i) => String(i.id) === String(installmentId));
  const at = idx >= 0 ? idx : 0;
  const earlier = items.slice(0, at).reduce((s, i) => s + Number(i.amount || 0), 0);
  const required = Number(items[at]?.amount || 0);
  const paid = Math.min(Math.max(paidTotal - earlier, 0), required);
  const unpaid = Math.max(required - paid, 0);
  let status = 'not_paid';
  if (paid > 0 && unpaid > 0) status = 'partially_paid';
  if (required > 0 && unpaid <= 0 && paid > 0) status = 'paid';
  return { required, paid, unpaid, status };
}

/**
 * Everything a receipt states, derived — never typed.
 *
 * THE BALANCE IS HISTORICAL. A receipt is a document a parent keeps, so it says
 * what was outstanding AT THE MOMENT that payment was made. Reprinting it after
 * further payments must not change what it says, or the copy in the school's
 * file and the copy in the parent's hand disagree.
 *
 * So `paidToDate` counts only completed payments made up to and including this
 * one — ordered by createdAt, with the id breaking ties because an id is stable
 * where a timestamp written by two paths may not be.
 *
 * NOTHING HERE IS ENTERED BY HAND. An admin typing a balance can contradict the
 * ledger, and then nobody can tell which is right.
 *
 * This replaces the page's old arithmetic, which compared ONE payment against
 * the whole fee — so every payment after the first showed a wrong balance and
 * a receipt for a fully-settled fee read "incomplete".
 */
export function buildReceipt({ payment, structure, priorPayments, installmentId }) {
  const amountPaid = Number(payment?.amount || 0);
  const at = payment?.createdAt ? new Date(payment.createdAt).getTime() : null;

  // PAYMENT IDS ARE UUIDS, not integers — `payments.id` is a uuid column, and
  // live data proved it: Number(uuid) is NaN, every NaN comparison is false, so
  // the payment being receipted failed its own tie-break and was left out of
  // its own total. A receipt for a 10,000 payment printed "paid to date 0".
  //
  // So identity is compared as a STRING, and ordering falls back to a stable
  // comparison that works for either kind of id.
  const sameId = (a, b) => String(a ?? '') === String(b ?? '');
  const idOrder = (a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na < nb;
    return String(a ?? '') < String(b ?? '');
  };

  const upToNow = (Array.isArray(priorPayments) && priorPayments.length
    ? priorPayments
    : [payment]
  ).filter((x) => {
    // This payment always counts towards its own receipt, whatever its status
    // and whatever the caller passed in the list.
    if (sameId(x?.id, payment?.id)) return true;
    if (String(x?.status || '') !== 'completed') return false;
    const xa = x?.createdAt ? new Date(x.createdAt).getTime() : null;
    if (at == null || xa == null) return idOrder(x?.id, payment?.id);
    if (xa !== at) return xa < at;
    return idOrder(x?.id, payment?.id);
  });

  const paidToDate = upToNow.reduce((n, x) => n + Number(x?.amount || 0), 0);

  const fee = structure || payment?.FeeStructure || null;
  const derived = fee
    ? feeStatusFor({ totalPaid: paidToDate, structure: fee, installmentId: installmentId || null })
    : null;
  const feeTotal = derived ? Number(derived.required || 0) : 0;
  const balance = derived ? Number(derived.unpaid || 0) : 0;
  const creditedHere = derived ? Number(derived.paid || 0) : paidToDate;

  return {
    receiptNo: payment?.id ?? null,
    date: payment?.createdAt ? String(payment.createdAt).slice(0, 10) : '',
    studentName: payment?.Student
      ? `${payment.Student.firstName || ''} ${payment.Student.lastName || ''}`.trim() : '',
    studentNumber: payment?.Student?.studentNumber || '',
    className: payment?.Class?.name || '',
    feeName: fee?.name || fee?.description || '',
    feeTotal,
    amountPaid,
    paidToDate: creditedHere,
    balance,
    // A fee with no total recorded cannot be called partially paid — there is
    // nothing to be short of.
    status: feeTotal > 0 && balance > 0 ? 'partially_paid' : 'paid',
    payerName: payment?.payerName || '',
    notes: payment?.notes || '',
    currency: payment?.currency || 'XAF',
  };
}

/**
 * A receipt as printable HTML, built from buildReceipt's output so the printed
 * document, the downloaded one and what is on screen cannot disagree.
 *
 * Both Print and Download use THIS. The download previously wrote the dialog's
 * innerHTML with no stylesheet, so the saved file looked nothing like the
 * printed one.
 *
 * No METHOD and no REFERENCE row: for a school taking cash they are noise, and
 * a receipt is about what was paid and what remains.
 */
export function receiptHtml({ receipt, schoolName, labels = {} }) {
  const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const money = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const row = (k, v) => (v || v === 0 ? `<tr><td class="k">${esc(k)}</td><td>${esc(v)}</td></tr>` : '');
  const cash = (k, v) => `<tr><td class="k">${esc(k)}</td><td>${money(v)}</td></tr>`;
  const r = receipt || {};
  const partly = r.status === 'partially_paid';

  return `<html><head><meta charset="utf-8"><style>
    @page { size: A5; margin: 12mm; }
    body { font-family: -apple-system, Roboto, Helvetica, sans-serif; color: #111; }
    h1 { font-size: 16px; margin: 0; }
    .sub { font-size: 11px; color: #555; margin: 2px 0 14px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    td { padding: 5px 4px; border-bottom: 1px solid #eee; }
    td.k { color: #555; width: 46%; }
    .total { margin-top: 14px; font-size: 18px; font-weight: 700; }
    .status { display: inline-block; margin-top: 10px; padding: 4px 10px; border-radius: 4px;
              font-size: 11px; font-weight: 700; letter-spacing: .04em;
              color: ${partly ? '#8A5A00' : '#12603C'}; background: ${partly ? '#FDF1D8' : '#E4F5EC'}; }
    .bal { margin-top: 6px; font-size: 12px; color: ${partly ? '#8A5A00' : '#555'}; }
    .foot { margin-top: 18px; font-size: 10px; color: #666; }
  </style></head><body>
    <h1>${esc(schoolName || '')}</h1>
    <p class="sub">${esc(labels.title || 'Payment receipt')}</p>
    <table>
      ${row(labels.receiptNo || 'Receipt no', r.receiptNo)}
      ${row(labels.date || 'Date', r.date)}
      ${row(labels.student || 'Student', r.studentName)}
      ${row(labels.studentNumber || 'Student no', r.studentNumber)}
      ${row(labels.class || 'Class', r.className)}
      ${row(labels.fee || 'Fee', r.feeName)}
      ${r.feeTotal ? cash(labels.feeTotal || 'Fee total', r.feeTotal) : ''}
      ${cash(labels.paidToDate || 'Paid to date', r.paidToDate)}
      ${r.feeTotal ? cash(labels.balance || 'Balance', r.balance) : ''}
      ${row(labels.payer || 'Paid by', r.payerName)}
      ${row(labels.notes || 'Note', r.notes)}
    </table>
    <p class="total">${esc(labels.amount || 'Amount paid')}: ${money(r.amountPaid)} ${esc(r.currency)}</p>
    <p class="status">${esc(partly
      ? (labels.partiallyPaid || 'PARTIALLY PAID')
      : (labels.fullyPaid || 'FULLY PAID'))}</p>
    ${partly ? `<p class="bal">${esc(labels.stillOwing || 'Still outstanding')}: ${money(r.balance)} ${esc(r.currency)}</p>` : ''}
    <p class="foot">${esc(labels.footer || 'This receipt confirms a payment recorded and approved by the school.')}</p>
  </body></html>`;
}

/**
 * A receipt belongs only to a COMPLETED payment.
 *
 * The web offered View on any row, so a receipt could be issued for a pending
 * or failed one — a piece of paper stating money was received that the school
 * has not confirmed, which is the whole reason the approval step exists.
 */
export function canIssueReceipt(payment) {
  return String(payment?.status || '') === 'completed';
}
